/**
 * REST istemcisi. Access token yalnız bellekte; refresh `halkora_rt` HttpOnly çerezinde.
 * 401 → bir kez `/v1/auth/refresh`, sonra istek yinelenir; refresh de düşerse oturum kaybı bildirilir.
 */
import type { ApiError as ApiErrorBody, AuthSession, ErrorCode } from "@halkora/api-types";

export const API_BASE: string = import.meta.env.VITE_API_BASE ?? "";

let accessToken: string | null = null;
let tokenExpiresAt = 0;
const authLostListeners = new Set<() => void>();

export const getAccessToken = (): string | null => accessToken;
export function setSession(s: Pick<AuthSession, "access_token" | "expires_in"> | null): void {
  accessToken = s?.access_token ?? null;
  tokenExpiresAt = s ? Date.now() + s.expires_in * 1000 : 0;
}
export const onAuthLost = (fn: () => void): (() => void) => {
  authLostListeners.add(fn);
  return () => authLostListeners.delete(fn);
};

import { enqueueClientError } from "../lib/errorReporter.ts";

export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode | "NETWORK";
  readonly body?: ApiErrorBody;
  readonly requestId?: string;
  constructor(status: number, body?: ApiErrorBody, fallbackCode: ErrorCode | "NETWORK" = "INTERNAL", requestId?: string) {
    super(body?.message ?? `HTTP ${status}`);
    this.status = status;
    this.code = body?.code ?? fallbackCode;
    this.body = body;
    this.requestId = body?.request_id ?? requestId;
  }
  fieldError(field: string): string | undefined {
    return this.body?.errors?.find((e) => e.field === field)?.message;
  }
}

/** 403 FORBIDDEN ve EMAIL_NOT_VERIFIED hatasını ayıklar (G22/G25). */
export function isEmailNotVerified(err: unknown): boolean {
  if (err instanceof ApiError) {
    if (err.status === 403 && err.code === "FORBIDDEN") {
      return Boolean(err.body?.errors?.some((e) => e.code === "EMAIL_NOT_VERIFIED"));
    }
  }
  return false;
}

let refreshing: Promise<boolean> | null = null;

/** Sekmeler arası refresh kilidinin adı (Web Locks API, aynı köken). */
export const REFRESH_LOCK = "halkora:refresh";
/**
 * Tek refresh isteğinin üst süresi. Sunucudaki hoşgörü penceresiyle (services/halkora/internal/auth/handler.go
 * RefreshGrace = 20 sn) birlikte düşünülür: sunucu token'ı döndürüp yanıt kaybolursa (zaman aşımı/ağ), aynı eski
 * çerezle yapılan yeniden deneme pencere içinde kalmalı, yoksa yeniden kullanım sayılıp aile iptal edilir.
 * 8 sn × en çok 2 deneme = 16 sn < 20 sn. Biri değişirse diğeri de gözden geçirilir.
 */
export const REFRESH_TIMEOUT_MS = 8_000;
/** Ağ hatası/zaman aşımında yeniden deneme sayısı (HTTP yanıtı geldiyse denenmez). */
export const REFRESH_RETRIES = 1;
/** Başka sekmenin kilidi bırakmasını bekleme üst süresi (onun iki denemesi sığar); aşılırsa kilitsiz devam. */
export const REFRESH_LOCK_WAIT_MS = 2 * REFRESH_TIMEOUT_MS + 2_000;

/** Zamanlayıcıyla iptal edilen sinyal (sahte zamanlayıcılarla test edilebilir; AbortSignal.timeout değil). */
function timeoutSignal(ms: number): { signal: AbortSignal; done(): void } {
  const ac = new AbortController();
  const t = setTimeout(() => ac.abort(new DOMException("zaman aşımı", "TimeoutError")), ms);
  return { signal: ac.signal, done: () => clearTimeout(t) };
}

/**
 * Sekmeler arası tek uçuş (#7): refresh token her kullanımda döndüğü için iki sekmenin aynı çerezle aynı anda
 * yenilemesi sunucuda yeniden kullanım sayılabilir. Kilit altında sırayla gidilir; bekleyen sekme kilidi
 * aldığında tarayıcı çerezi zaten güncellenmiştir, kendi rotasyonunu yeni çerezle yapar. Web Locks yoksa,
 * kilit beklemesi zaman aşımına uğrarsa ya da kilit reddedilirse kilitsiz çalışır (kalan yarışı sunucudaki
 * hoşgörü penceresi karşılar). fn hata fırlatmaz; bu yüzden catch yolunda iki kez koşmaz.
 */
function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== "undefined" ? (navigator as Navigator & { locks?: LockManager }).locks : undefined;
  if (!locks?.request) return fn();
  const wait = timeoutSignal(REFRESH_LOCK_WAIT_MS);
  return (locks.request(REFRESH_LOCK, { mode: "exclusive", signal: wait.signal }, () => {
    wait.done();
    return fn();
  }) as Promise<T>)
    .catch(() => fn())
    .finally(wait.done);
}

/** "ok" | "rejected" (sunucu yanıt verdi, oturum yok) | "network" (yanıt yok: ağ/zaman aşımı → yeniden denenebilir). */
async function refreshOnce(): Promise<"ok" | "rejected" | "network"> {
  const t = timeoutSignal(REFRESH_TIMEOUT_MS);
  try {
    const res = await fetch(`${API_BASE}/v1/auth/refresh`, { method: "POST", credentials: "include", signal: t.signal });
    if (!res.ok) return "rejected";
    setSession((await res.json()) as AuthSession);
    return "ok";
  } catch {
    return "network";
  } finally {
    t.done();
  }
}

async function doRefresh(): Promise<boolean> {
  for (let i = 0; i <= REFRESH_RETRIES; i++) {
    const r = await refreshOnce();
    if (r !== "network") return r === "ok";
  }
  return false;
}

/**
 * Paylaşımlı tek refresh: aynı sekmedeki eşzamanlı çağrılar tek promise'te birleşir, sekmeler arası kilitle
 * sıraya girer. Başarıda yeni access token belleğe yazılır.
 */
export function refreshSession(): Promise<boolean> {
  refreshing ??= withRefreshLock(doRefresh).finally(() => {
    setTimeout(() => (refreshing = null), 0);
  });
  return refreshing;
}

/** Süresi dolmak üzere ise (30 sn) yeniler; gateway IDENTIFY'dan önce çağrılır. */
export async function ensureFreshToken(): Promise<string | null> {
  if (accessToken && Date.now() < tokenExpiresAt - 30_000) return accessToken;
  if (!(await refreshSession())) return accessToken && Date.now() < tokenExpiresAt ? accessToken : null;
  return accessToken;
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** false: Bearer eklenmez, 401'de refresh denenmez (giriş/kayıt). */
  auth?: boolean;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  const s = qs.toString();
  return `${API_BASE}${path}${s ? `?${s}` : ""}`;
}

export async function requestWithHeaders<T>(path: string, opts: RequestOptions = {}): Promise<{ data: T; headers: Headers; status: number }> {
  const auth = opts.auth ?? true;
  // hk-izin: HTTP metodu ASCII standart
  const method = (opts.method ?? "GET").toUpperCase();
  const isIdempotent = method === "GET" || method === "HEAD";

  const doFetch = async (allowRetry = isIdempotent): Promise<Response> => {
    const headers: Record<string, string> = { ...opts.headers };
    if (opts.body !== undefined) headers["Content-Type"] = "application/json";
    if (auth && accessToken) headers.Authorization = `Bearer ${accessToken}`;
    try {
      const resp = await fetch(buildUrl(path, opts.query), {
        method,
        headers,
        body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
        credentials: "include",
        signal: opts.signal,
      });

      // Geçici reverse-proxy 502/503/504 durumlarında (ör. servis yeniden başlarken) tek seferlik otomatik telafi
      if (allowRetry && (resp.status === 502 || resp.status === 503 || resp.status === 504)) {
        await new Promise((r) => setTimeout(r, 400));
        return doFetch(false);
      }

      return resp;
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") throw e;
      // Ağ hatasında idempotent istekler için 350ms bekleyip bir kez daha dene
      if (allowRetry) {
        await new Promise((r) => setTimeout(r, 350));
        return doFetch(false);
      }

      const isOffline = typeof navigator !== "undefined" && !navigator.onLine;
      if (!isOffline && path !== "/v1/client-errors" && path !== "/v1/gateway") {
        enqueueClientError({
          message: `Ağ hatası: ${method} ${path}`,
          code: "NETWORK",
          route: path,
        });
      }
      throw new ApiError(0, undefined, "NETWORK");
    }
  };

  let res = await doFetch();
  if (res.status === 401 && auth) {
    if (await refreshSession()) res = await doFetch();
    else {
      authLostListeners.forEach((f) => f());
    }
  }
  if (res.status === 204) return { data: undefined as T, headers: res.headers, status: res.status };
  let json: unknown;
  const text = await res.text();
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = undefined;
    }
  }
  const reqId = res.headers.get("x-request-id") ?? undefined;
  if (!res.ok) {
    // 502, 503, 504 (altyapı/yeniden başlatma) ve gateway ucu istemci hata havuzuna yazılmaz
    const isTransientProxy = res.status === 502 || res.status === 503 || res.status === 504;
    if (res.status >= 500 && !isTransientProxy && path !== "/v1/client-errors" && path !== "/v1/gateway") {
      enqueueClientError({
        message: `Sunucu hatası (${res.status}): ${method} ${path}`,
        code: (json as ApiErrorBody | undefined)?.code ?? "INTERNAL",
        route: path,
        requestId: reqId ?? (json as ApiErrorBody | undefined)?.request_id,
        extra: { status: res.status },
      });
    }
    throw new ApiError(res.status, json as ApiErrorBody | undefined, "INTERNAL", reqId);
  }
  return { data: json as T, headers: res.headers, status: res.status };
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { data } = await requestWithHeaders<T>(path, opts);
  return data;
}
